import { useForm } from 'react-hook-form';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { TextareaInput } from '@/components/form/TextareaInput.tsx';
import { debugZodResolver } from '@/lib/debugZodResolver.ts';
import { AttentionItemDto } from '@/types/stats/AttentionItemDto.ts';
import useResolveAttentionFindings from '@/features/dashboard/attention/hooks/useResolveAttentionFindings.ts';
import {
  ResolveFindingFormData,
  resolveFindingSchema,
} from '@/features/dashboard/attention/schemas/resolveFindingSchema.ts';

interface ResolveFindingDialogProps {
  item: AttentionItemDto;
  /** Nagłówek sprawy z rejestru — dialog nie zna zdań, dostaje gotowy napis. */
  label: string;
  tourId?: string;
  onClose: () => void;
  /** Wołane po zapisie; toast z „Cofnij” składa rodzic, który zostaje zamontowany. */
  onResolved: (item: AttentionItemDto) => void;
}

/** Odłożenie sprawy z komentarzem; komentarz wymagany tylko, gdy backend tak oznaczył pozycję. */
const ResolveFindingDialog = ({
  item,
  label,
  tourId,
  onClose,
  onResolved,
}: ResolveFindingDialogProps) => {
  const resolve = useResolveAttentionFindings();
  const commentRequired = item.commentRequiredOnResolve;
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ResolveFindingFormData>({
    resolver: debugZodResolver(resolveFindingSchema(commentRequired)),
    defaultValues: { comment: '' },
  });

  // Zamknięcie w trakcie zapisu odmontowałoby dialog, a TanStack v5 nie woła wtedy
  // `onSuccess` przekazanego do `mutate` — toast z „Cofnij” by nie przyszedł.
  const close = () => {
    if (!resolve.isPending) onClose();
  };

  const onSubmit = (data: ResolveFindingFormData) => {
    resolve.mutate(
      {
        findingIds: item.findingIds,
        comment: data.comment || undefined,
        tourId,
      },
      { onSuccess: () => onResolved(item) },
    );
  };

  return (
    <Modal onClose={close} isDirty={isDirty} className="max-w-[520px]">
      {/* Nagłówek poza `<form>`: jego krzyżyk nie ma `type`, więc w formularzu wysłałby go. */}
      <ModalHeader
        title="Odłóż sprawę"
        description={label}
        onClose={close}
        closeDisabled={resolve.isPending}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalContent>
          <TextareaInput
            label="Komentarz"
            register={register('comment')}
            required={commentRequired}
            error={errors.comment?.message}
            hint={
              commentRequired
                ? 'Komentarz jest wymagany dla spraw o pieniądzach'
                : 'Dlaczego sprawa może poczekać'
            }
            rows={4}
          />
        </ModalContent>
        <ModalFooter>
          <ActionButton
            tone="ghost"
            size="sm"
            type="button"
            onClick={close}
            disabled={resolve.isPending}
          >
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={resolve.isPending}
          >
            Odłóż
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ResolveFindingDialog;
